import { ReactElement, useMemo, useEffect, useState } from "react";
import { Card, Text, Grid, AgGridReact, AgGrid, toast, Flex } from "@jsq/ds/core";
import { Icon } from "@jsq/ds/icons";
import { Colors } from "@jsq/ds/styles";
import { useFeatureFlag } from "@jsq/flags-react";
import { useUpdateEntityStatusMutation } from "~/graphql/supergraphHooks";
import { LoadingCellRenderer } from "~/pages/Billing/components/LoadingCellRenderer";
import { SPACING } from "~/styles/variables";
import { PAGINATION, BILLING_GRID_NAMES } from "../../constants";
import { useBillingDate } from "../../contexts/BillingDateContext";
import { useGridViewManager } from "../../hooks/useGridViewManager";
import { ClientDataGraphqlDataSource } from "../ClientDataGraphqlDataSource";
import { useEntityUpdate } from "../EntityUpdateContext";
import { useFilterSort } from "../FilterSortContext";
import { createDefaultEntitiesColumnDefs } from "./colDef";
import { EntitiesCardActions } from "./EntitiesCardActions";
import { createRenderEntityStatusCell } from "./EntityStatusCellRenderer";
import {
  entitiesCardContainer,
  entitiesTitle,
  tableContainer,
  entitiesStatusUpdateInfo,
  entitiesStatusUpdateInfoDescription,
} from "./styles_vanilla.css";
import {
  getRowId,
  updateGridDataSource,
  handleGridReady,
  createOnEntityStatusChange,
  createIsEntityStatusEditable,
  createHandleUpdateEntityStatusCompleted,
} from "./utils";

interface EntitiesCardLegacyProps {
  clientId: string;
}

export const EntitiesCardLegacy = ({ clientId }: EntitiesCardLegacyProps): ReactElement => {
  const { setFilters, setSorts } = useFilterSort();
  const { setHasActiveEntityUpdates } = useEntityUpdate();
  const { asOfDateString } = useBillingDate();
  const [gridApi, setGridApi] = useState<AgGrid.GridApi>();

  const isBillingEntityStatusUpdateFlagEnabled = useFeatureFlag(
    "billingEntityStatusUpdate",
    false,
  );
  const isBillingAgGridStaticClientDetailsSetFiltersFlagEnabled = useFeatureFlag(
    "billingAgGridStaticClientDetailsSetFilters",
    false,
  );
  const isBillingEnableFADataFlagEnabled = useFeatureFlag("billingEnableFAData", false);
  const isSafeCaseIdAndActiveInvestmentsEnabled = useFeatureFlag(
    "billingSafeCaseIdAndActiveInvestments",
    false,
  );
  const isBillingSaveUserGridViewFlagEnabled = useFeatureFlag("billingSaveUserGridView", false);

  const handleUpdateEntityStatusCompleted = createHandleUpdateEntityStatusCompleted(
    gridApi,
    setHasActiveEntityUpdates,
  );

  const [updateEntityStatusMutation, { loading: updateLoading }] =
    useUpdateEntityStatusMutation({
      onCompleted: handleUpdateEntityStatusCompleted,
      onError: (error) => {
        toast.error(`${error.message}`);
      },
    });

  const onEntityStatusChange = useMemo(
    () => createOnEntityStatusChange(updateEntityStatusMutation, clientId),
    [updateEntityStatusMutation, clientId],
  );

  const renderEntityStatusCell = useMemo(
    () => createRenderEntityStatusCell(updateLoading),
    [updateLoading],
  );

  const isEntityStatusEditable = useMemo(
    () => createIsEntityStatusEditable(updateLoading),
    [updateLoading],
  );

  const defaultColumnDefs = useMemo(
    () =>
      createDefaultEntitiesColumnDefs({
        isBillingEntityStatusUpdateFlagEnabled,
        isBillingAgGridStaticClientDetailsSetFiltersFlagEnabled,
        isBillingEnableFADataFlagEnabled,
        isSafeCaseIdAndActiveInvestmentsEnabled,
        renderEntityStatusCell,
        isEntityStatusEditable,
        onEntityStatusChange,
      }),
    [
      isBillingEntityStatusUpdateFlagEnabled,
      isBillingAgGridStaticClientDetailsSetFiltersFlagEnabled,
      isBillingEnableFADataFlagEnabled,
      isSafeCaseIdAndActiveInvestmentsEnabled,
      renderEntityStatusCell,
      isEntityStatusEditable,
      onEntityStatusChange,
    ],
  );

  const {
    columnDefs: entitiesColumnDefs,
    shouldShowSaveViewButton,
    shouldShowClearFilters,
    shouldShowGrid,
    saveViewLoading,
    handleSavedViewClicked,
    handleClearFiltersClicked,
    handleOnColumnMoved,
    handleOnColumnVisible,
    handleOnColumnResized,
    handleOnFilterChanged,
  } = useGridViewManager({
    gridName: BILLING_GRID_NAMES.CLIENT_ARENA_ENTITIES,
    defaultColumnDefs: defaultColumnDefs,
    gridApi,
    isBillingSaveUserGridViewFlagEnabled,
  });

  const dataSource = useMemo(() => {
    if (!clientId) return null;
    return new ClientDataGraphqlDataSource(clientId, {
      onFiltersChange: setFilters,
      onSortsChange: setSorts,
    });
  }, [clientId, setFilters, setSorts]);

  useEffect(() => {
    if (dataSource) {
      dataSource.setAsOfDate(asOfDateString);
    }
    updateGridDataSource(gridApi, dataSource);
  }, [gridApi, dataSource, asOfDateString]);

  const onGridReady = (event: AgGrid.GridReadyEvent) => {
    handleGridReady(event, setGridApi);
  };

  const gridOptions: AgGridReact.AgGridReactProps["gridOptions"] = useMemo(() => {
    const options: AgGridReact.AgGridReactProps["gridOptions"] = {
      suppressCellFocus: true,
      rowHeight: 48,
      domLayout: "normal",
      suppressHorizontalScroll: false,
      rowModelType: "serverSide",
      getRowId,
      pagination: true,
      paginationPageSizeSelector: false,
      paginationPageSize: PAGINATION.CLIENT_ENTITIES_PAGE_SIZE,
      cacheBlockSize: PAGINATION.CLIENT_ENTITIES_PAGE_SIZE,
      onGridReady: onGridReady,
      tooltipShowDelay: 1000,
      defaultColDef: {
        menuTabs: ["generalMenuTab", "filterMenuTab"],
      },
      sideBar: {
        toolPanels: [
          {
            id: "columns",
            labelDefault: "Columns",
            labelKey: "columns",
            iconKey: "columns",
            toolPanel: "agColumnsToolPanel",
            toolPanelParams: {
              suppressRowGroups: true,
              suppressValues: true,
              suppressPivots: true,
              suppressPivotMode: true,
              suppressColumnFilter: false,
              suppressColumnSelectAll: false,
              suppressColumnExpandAll: true,
            },
          },
        ],
        position: "right",
      },
    };

    if (isBillingSaveUserGridViewFlagEnabled) {
      options.onColumnMoved = handleOnColumnMoved;
      options.onColumnVisible = handleOnColumnVisible;
      options.onColumnResized = handleOnColumnResized;
      options.onFilterChanged = handleOnFilterChanged;
    }

    return options;
  }, [
    isBillingSaveUserGridViewFlagEnabled,
    handleOnColumnMoved,
    handleOnColumnVisible,
    handleOnColumnResized,
    handleOnFilterChanged,
  ]);

  if (!dataSource) {
    return (
      <Card className={entitiesCardContainer}>
        <Text variant="h3" className={entitiesTitle}>
          Entities
        </Text>
        <Text>Loading entity data...</Text>
      </Card>
    );
  }

  return (
    <Card className={entitiesCardContainer}>
      <Flex justifyContent="space-between" alignItems="center" className={entitiesTitle}>
        <Text variant="h3">Entities</Text>
        <EntitiesCardActions
          shouldShowSaveViewButton={shouldShowSaveViewButton}
          shouldShowClearFilters={shouldShowClearFilters}
          isBillingSaveUserGridViewFlagEnabled={isBillingSaveUserGridViewFlagEnabled}
          saveViewLoading={saveViewLoading}
          handleSavedViewClicked={handleSavedViewClicked}
          handleClearFiltersClicked={handleClearFiltersClicked}
        />
      </Flex>
      {isBillingEntityStatusUpdateFlagEnabled && (
        <Card className={entitiesStatusUpdateInfo}>
          <Flex gap={SPACING.SMALL} alignItems="center">
            <Icon
              type="info-circle"
              variant="solid"
              color={Colors.Informative600}
              size="large"
              data-testid="entity-status-update-info-icon"
            />
            <Text className={entitiesStatusUpdateInfoDescription}>
              Edits made to the entity status will be visible the following day
            </Text>
          </Flex>
        </Card>
      )}
      <div className={tableContainer} data-testid="entities-card-grid">
        {shouldShowGrid && entitiesColumnDefs && (
          <Grid
            gridOptions={gridOptions}
            columnDefs={entitiesColumnDefs}
            loadingCellRenderer={LoadingCellRenderer}
          />
        )}
      </div>
    </Card>
  );
};
